import { StringTMap } from '@@types/common';
import Department, { DepartmentRef } from '@@types/Department';

import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { lift } from '~/utils/monadUtil';
import { action, computed, observable } from 'mobx';
import curry from 'ramda/es/curry';
import identity from 'ramda/es/identity';
import mergeRight from 'ramda/es/mergeRight';
import pipe from 'ramda/es/pipe';
import prop from 'ramda/es/prop';
import sortBy from 'ramda/es/sortBy';
import union from 'ramda/es/union';
import values from 'ramda/es/values';
import { Maybe } from 'ramda-fantasy';

class OrganizationChartStore {
  @observable
  activeDepartmentId = '';

  @observable
  defaultDepartmentId = '';

  @observable
  departmentMap: StringTMap<Department> = {};

  @observable
  includeChildMap = {};

  @observable
  membersPage = 0;

  @observable
  showPrimaryDepartments = false;

  @observable
  primaryDepartmentIds: string[] = [];

  @computed
  get activeDepartment() {
    return this.departmentMap[this.activeDepartmentId];
  }

  @computed
  get defaultDepartment() {
    return this.departmentMap[this.defaultDepartmentId];
  }

  @computed
  get departments() {
    return pipe(values, sortBy(prop('name')))(this.departmentMap);
  }

  @computed
  get primaryDepartments() {
    return this.primaryDepartmentIds.map((id) => this.departmentMap[id]);
  }

  @computed
  get canShowPrimaryDepartments() {
    return this.primaryDepartmentIds.length > 1 && globalSettingStore.organizationChartGeneral.visibility === 'tenant';
  }

  @action
  appendMembers = curry((departmentId: string, members: string[]) => {
    this.departmentMap[departmentId].members = union(this.departmentMap[departmentId]?.members ?? [], members);
  });

  @action
  setDepartmentMap = (departmentMap) => {
    this.departmentMap = departmentMap;
  };

  @action
  setDepartmentMemberCount = curry((departmentId, totalCount) => {
    this.departmentMap[departmentId].memberCount = totalCount;
  });

  @action
  setIncludeChild = curry((departmentId, includeChild) => {
    this.includeChildMap[departmentId] = includeChild;
  });

  @action
  setIsFetchingMembers = curry((departmentId: string, value: boolean) => {
    this.departmentMap[departmentId].isFetchingMembers = value;
  });

  @action
  setMembersPage = (page) => {
    this.membersPage = page;
  };

  @action
  setSubDepartmentsId = curry((parentId, departmentsId) => {
    this.departmentMap[parentId].subDepartmentsId = departmentsId;
  });

  @action
  setPrimaryDepartmentIds = (primaryDepartmentIds: string[]) => {
    this.primaryDepartmentIds = primaryDepartmentIds;
  };

  @action
  setDepartment = (department: Department, __?: string, __2?: StringTMap<Department>) => {
    if (department) {
      this.departmentMap[department.id] = mergeRight(this.departmentMap[department.id], department);
    }
  };

  @action
  mergeDepartmentMap = (departmentMap: StringTMap<DepartmentRef>) => {
    const departmentIds = Object.keys(departmentMap);
    departmentIds.forEach((id) => {
      this.departmentMap[id] = this.departmentMap[id]
        ? mergeRight(this.departmentMap[id], departmentMap[id])
        : departmentMap[id];
    });
  };

  @action
  setActiveDepartmentId = (departmentId: string) => {
    this.activeDepartmentId = departmentId;
  };

  @action
  setDefaultDepartmentId = (departmentId: string) => {
    this.defaultDepartmentId = departmentId;
  };

  @action
  setShowPrimaryDepartments = (show: boolean) => {
    this.showPrimaryDepartments = show;
    if (show) {
      this.activeDepartmentId = '';
    }
  };

  getDepartmentById = (departmentId) => {
    return pipe<any, any, any>(prop(departmentId), lift(identity))(this.departmentMap) as Maybe<Department>;
  };

  getIncludeChildById = (departmentId) => {
    return this.includeChildMap[departmentId] || false;
  };
}

export default OrganizationChartStore;

export const organizationChartStore = new OrganizationChartStore();
